import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/edit_career_profile/submit_application_form.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { getPendingOrSuccessAoiRecord, getProgramApplication, convertCurrentUserFromIguana } from 'Users';
import { ProgramFamily } from 'Program';
import { PostApplyInterviewModalType, POST_APPLY_INTERVIEW_MODAL_SESSION_STORAGE_KEY } from 'ProgramApplication';
import { buildFormCompletionInput, getFormsCompletePercent, getFirstIncompleteForm } from 'FormCompletionHelper';
import { getLaunchableCoachSessionOpportunityForUser } from 'TutorBotCoach';
import determineProgramPreferencesStateKey from '../../../determineProgramPreferencesStateKey';
import programPreferencesConfigs from '../../../programPreferencesConfigs';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('submitApplicationForm', [
    '$injector',
    function factory($injector) {
        const DialogModal = $injector.get('DialogModal');
        const TranslationHelper = $injector.get('TranslationHelper');
        const ngToast = $injector.get('ngToast');
        const $timeout = $injector.get('$timeout');
        const AuthFormHelperMixin = $injector.get('AuthFormHelperMixin');
        const NavigationHelperMixin = $injector.get('Navigation.NavigationHelperMixin');
        const FormHelper = $injector.get('FormHelper');
        const EditCareerProfileHelper = $injector.get('EditCareerProfileHelper');
        const $http = $injector.get('$http');
        const Cohort = $injector.get('Cohort');
        const $q = $injector.get('$q');
        const $window = $injector.get('$window');

        return {
            restrict: 'E',
            // see STEP_FORM_ATTRS in edit_career_profile_dir.js
            scope: {
                // form-step-validity reads this by name off the scope. See STEP_FORM_ATTRS.
                $setValidity: '<setValidity',
                careerProfile: '<',
                currentForm: '<',
                currentUser: '<',
                formContext: '<',
                programFamilyFormDatum: '<',
                programType: '<',
                steps: '<',
                stepsProgressMap: '<',
                goToStep: '&',
            },
            templateUrl,
            link(scope) {
                //---------------------------
                // Mixins
                //---------------------------

                FormHelper.supportForm(scope, scope.submit_application);
                NavigationHelperMixin.onLink(scope);
                AuthFormHelperMixin.onLink(scope);

                //---------------------------
                // Mixin Initialization
                //---------------------------

                const translationHelper = new TranslationHelper('careers.edit_career_profile.submit_application_form');

                //---------------------------
                // Scope Properties
                //---------------------------
                Object.defineProperty(scope, 'applicationComplete', {
                    get() {
                        // Read the saved values, not the proxies. The applicant has to save a step before it
                        // counts toward a complete application.
                        const input = buildFormCompletionInput(scope.currentUser, scope.steps);
                        return (
                            getFormsCompletePercent(scope.steps, input) === 100 ||
                            getFirstIncompleteForm(scope.steps, input)?.stepName === 'submit_application'
                        );
                    },
                    configurable: true,
                });

                Object.defineProperty(scope, 'optedInToScholarshipConsideration', {
                    get() {
                        return scope.programFamilyFormDatum?.scholarshipLabelsInterestedIn?.length;
                    },
                    configurable: true,
                });

                //---------------------------
                // Scope Initialization
                //---------------------------

                EditCareerProfileHelper.logEventForApplication(scope.formContext, 'viewed-submit_application');

                // I tried putting this directly into the ng-click, but it would not work for some reason
                scope.toggleFullProfile = () => {
                    scope.showFullProfile = !scope.showFullProfile;
                };

                // See how programType is set in edit_career_profile_dir.js using `determineTargetProgramType`
                // If no programType is selected over there, then the user cannot submit the application here.
                if (scope.programType) {
                    $http
                        .get(`${$window.ENDPOINT_ROOT}/api/cohorts/promoted/${scope.programType}.json`)
                        .then(response => {
                            scope.cohort = Cohort.new(response.data.contents.cohorts[0]);
                        });
                }

                Object.defineProperty(scope, 'applicationDeadlineMessage', {
                    get() {
                        return scope.cohort.getApplicationDeadlineMessage();
                    },
                    configurable: true,
                });

                //---------------------------
                // Submit Application
                //---------------------------

                function showPostSubmitModal() {
                    return $q(resolve => {
                        const modalOptions = {
                            content: '<thanks-for-applying-modal></thanks-for-applying-modal>',
                            title: new TranslationHelper('careers.thanks_for_applying_modal').get('title'),
                            size: 'small',
                            close: resolve,
                        };

                        DialogModal.alert({
                            ...modalOptions,
                            close: resolve,
                        });
                    });
                }

                function forwardToVoiceCoach() {
                    scope.loadRoute('/coach');

                    // Since we're already redirecting to the coach page, we don't
                    // want to do the redirect that normally in the response to this promise
                    return { skipRedirect: true };
                }

                function onAfterApplicationSubmitted() {
                    const programApplication = getProgramApplication(scope.currentUser);

                    // If user is eligible for a post-apply interview modal (determined server-side),
                    // redirect to dashboard where the modal will be shown. Don't show any modal here.
                    // We only set a one-time trigger flag; all the data the modal needs (locale keys,
                    // calendar) is derived on the dashboard from the program application itself
                    // (see usePostApplyInterviewModal.ts).
                    const modalType = programApplication?.postApplyInterviewModalType;
                    if (
                        modalType === PostApplyInterviewModalType.Instant ||
                        modalType === PostApplyInterviewModalType.Optional
                    ) {
                        sessionStorage.setItem(POST_APPLY_INTERVIEW_MODAL_SESSION_STORAGE_KEY, 'true');
                        return undefined;
                    }

                    // @ts-check
                    /**  @type {import('TutorBotCoach').CoachSessionOpportunity} */
                    const opportunity = getLaunchableCoachSessionOpportunityForUser(
                        convertCurrentUserFromIguana(scope.currentUser),
                    );
                    if (
                        opportunity &&
                        opportunity.sessionType === 'post_application_goal_setting' &&
                        opportunity.programApplicationId === programApplication?.id
                    ) {
                        return forwardToVoiceCoach();
                    }
                    return showPostSubmitModal();
                }

                scope.pendingApplication = getPendingOrSuccessAoiRecord(scope.currentUser);
                const hasPendingApplication = !!scope.pendingApplication;
                const showApplicationUpdatedToast = () => {
                    ngToast.create({
                        content: translationHelper.get('application_updated'),
                        className: 'success',
                    });
                };

                scope.submitApplication = () => {
                    scope.submitting = true;
                    let promise;

                    // save career profile because the phone field is on this page
                    scope.careerProfile.save().then(() => {
                        // The unsaved changes confirmation dialog modal pops up when the route changes but we see that the
                        // form on the page is still dirty. At this point in the logic, the user has successfully saved
                        // their career profile, so they shouldn't have any unsaved changes, but the form may still dirty
                        // so we need to set the currentForm to pristine to prevent the unsaved changes confirmation dialog
                        // modal from popping up unexpectedly.
                        scope.currentForm?.$setPristine();
                        scope.currentUser.career_profile = scope.careerProfile;
                        const admissionsValarOptInValueChanged =
                            scope.config.showValarOptIn() &&
                            scope.pendingApplication?.admissionsValarOptIn !== scope.valarOptInProxy?.value;

                        if (hasPendingApplication && admissionsValarOptInValueChanged) {
                            promise = $http
                                .post(
                                    `${$window.ENDPOINT_ROOT}/api/user_actions/program_application/change_settings.json`,
                                    {
                                        record_id: scope.pendingApplication.id,
                                        admissions_valar_opt_in: scope.valarOptInProxy?.value,
                                    },
                                )
                                .then(showApplicationUpdatedToast);
                        } else if (hasPendingApplication) {
                            // We don't actually have to update anything here, but the timeout
                            // is necessary because the toast won't reliably show up unless defer a digest
                            promise = $timeout().then(showApplicationUpdatedToast);
                        } else {
                            // Server calculates instant interview modal eligibility
                            promise = scope.currentUser
                                .applyToCohort(scope.cohort, scope.valarOptInProxy?.value ?? false)
                                .then(() => onAfterApplicationSubmitted());
                        }

                        return promise
                            .then(result => {
                                if (result?.skipRedirect) return;
                                scope.loadRoute(hasPendingApplication ? '/settings/application' : '/dashboard');
                            })
                            .finally(() => {
                                scope.submitting = false;
                            });
                    });
                };

                scope.$watchGroup(['careerProfile', 'currentUser'], () => {
                    if (scope.careerProfile && scope.currentUser) {
                        const stateKey = determineProgramPreferencesStateKey(scope.careerProfile);
                        const configs = programPreferencesConfigs($injector, scope.currentUser, scope.programType);
                        scope.config = configs[stateKey];
                        scope.submissionDisabled = !!scope.config.saveButtonsDisabled;
                        scope.showProgramPreferencesBlurb =
                            scope.applicationComplete &&
                            // If the program preference config has deemed that the save button for this form should be disabled,
                            // we show the program preferences blurb to give the user some UI telling them why they can't submit
                            // their application.
                            (scope.submissionDisabled ||
                                // Otherwise, we only show the program preferences blurb in the context of the master_of_business_administration
                                // program family, because we use it to explain the different programs the application will be considered for.
                                scope.currentUser.relevantCohort.programFamily ===
                                    ProgramFamily.master_of_business_administration);
                        handleValarOptIn();
                    }
                });

                scope.interviewTimePreferences = ['weekdays', 'weekend', 'no_pref'];

                function handleValarOptIn() {
                    if (!scope.config.showValarOptIn()) return;

                    // default the checkbox to checked unless there is already a stored boolean value
                    scope.valarOptInProxy = { value: scope.pendingApplication?.admissionsValarOptIn ?? true };
                }
            },
        };
    },
]);
